/**
 * Card global de chamada ENTRANTE tocando (Fase 3 VoIP) — canto superior
 * direito, em qualquer rota. Atender passa a ligação pra sessão global
 * (`call-session`), que mostra a janelinha flutuante no mesmo lugar;
 * Recusar manda a recusa real (quem liga vê a chamada cair).
 *
 * Com outra ligação no ar, o card desce (pra não cobrir a janela da ligação,
 * que nasce no mesmo canto) e o botão vira "Encerrar e atender".
 */
import { apiRequest } from '@/shared/lib/api-client';
import { Phone, PhoneOff } from 'lucide-react';
import { useEffect } from 'react';
import { useCallSession } from './call-session';
import { useIncomingCallStore } from './incoming-call-store';
import { stopRingtone } from './ringtone';

export function IncomingCallOverlay() {
  const call = useIncomingCallStore((s) => s.call);
  const clear = useIncomingCallStore((s) => s.clear);
  const { busy, answerIncoming } = useCallSession();

  // Segurança: se o evento de fim se perder, o toque não fica eterno.
  useEffect(() => {
    if (!call) return;
    const timer = setTimeout(() => {
      stopRingtone();
      useIncomingCallStore.getState().clear();
    }, 75_000);
    return () => clearTimeout(timer);
  }, [call]);

  const handleAnswer = (): void => {
    if (!call) return;
    stopRingtone();
    clear();
    answerIncoming(call.call_id, call.contact_name);
  };

  const handleReject = (): void => {
    if (!call) return;
    stopRingtone();
    clear();
    void apiRequest('/voip/reject', {
      method: 'POST',
      body: { call_id: call.call_id },
    }).catch(() => undefined);
  };

  if (!call) return null;

  return (
    <div
      className={`fixed right-4 z-[100] w-[300px] rounded-md bg-white p-4 shadow-lg ring-1 ring-n-200 ${
        busy ? 'top-[96px]' : 'top-2'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 animate-pulse items-center justify-center rounded-pill bg-success-50 text-success-600">
          <Phone size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-bold text-[14px] text-n-900">{call.contact_name}</div>
          <div className="truncate text-[12px] text-n-500">
            {call.contact_phone ? `+${call.contact_phone}` : 'Chamada de voz'}
            {call.channel_name ? ` · ${call.channel_name}` : ''}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={handleReject}
          className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md bg-danger-500 font-semibold text-[13px] text-white transition-colors hover:bg-danger-600"
        >
          <PhoneOff size={14} /> Recusar
        </button>
        <button
          type="button"
          onClick={handleAnswer}
          className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md bg-success-500 font-semibold text-[13px] text-white transition-colors hover:bg-success-600"
        >
          <Phone size={14} /> {busy ? 'Encerrar e atender' : 'Atender'}
        </button>
      </div>
    </div>
  );
}
